import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EventKind } from '@/db/schema/events';
import type { SoldierRole } from '@/db/schema/soldiers';
import type { UserRole } from '@/db/schema/users';
import { useToast } from '@/hooks/use-toast';
import { apiClient, unwrap } from '@/lib/api-client';
import { createCrudHooks } from './create-crud-hooks';
import { firstErrorMessage } from './term-versions';

/* CRUD genérico continua igual (telas dinâmicas e selects de outras telas usam). */
export const {
  keys: bataillonKeys,
  useList: useBataillons,
  useDetail: useBataillon,
  useCreate: useCreateBataillon,
  useUpdate: useUpdateBataillon,
  useDelete: useDeleteBataillon,
} = createCrudHooks(apiClient.bataillons, 'bataillons');

/* ───────────── Tipos da tela ───────────── */

export type CommandPerson = {
  id: string;
  name: string;
  photo?: string | null;
  image?: string | null;
  soldierNumber?: number | null;
  role?: string;
};
export type TaskPhase = 'before' | 'during' | 'after';
export const TASK_PHASE_LABEL: Record<TaskPhase, string> = {
  before: 'Antes do retiro',
  during: 'No retiro',
  after: 'Pós-retiro',
};
export const TASK_PHASE_ORDER: TaskPhase[] = ['before', 'during', 'after'];

export type BataillonTask = {
  id: string;
  title: string;
  phase: TaskPhase;
  done: boolean;
  position: number;
  doneAt: string | null;
};
export type BataillonMember = {
  id: string;
  name: string;
  photo: string | null;
  role?: SoldierRole;
  userId?: string | null;
  birthday: string | Date;
  city: string;
  soldierNumber: number | null;
  paid: boolean | null;
  bataillonId: string | null;
  bataillonTag?: string | null;
  /** Tipo do evento do recruta (só na ficha): `kids` → Kids, o resto → Jovens. */
  kind?: EventKind | null;
};
export type BataillonOverview = {
  id: string;
  name: string;
  tag: string | null;
  isActive: boolean;
  major: CommandPerson | null;
  kidsMajor: CommandPerson | null;
  kidsCaptain: CommandPerson | null;
  youthCaptain: CommandPerson | null;
  soldiersCount: number;
  tasksTotal: number;
  tasksDone: number;
};
export type BataillonDetail = Omit<
  BataillonOverview,
  'soldiersCount' | 'tasksTotal' | 'tasksDone'
> & {
  majorId: string | null;
  kidsMajorId: string | null;
  kidsCaptainId: string | null;
  youthCaptainId: string | null;
  description: string | null;
  tasks: BataillonTask[];
  soldiers: BataillonMember[];
};

const api = apiClient.bataillons as any;

export const bataillonBoardKeys = {
  all: ['bataillons', 'board'] as const,
  overview: (eventId?: string) =>
    [...bataillonBoardKeys.all, 'overview', eventId ?? 'all'] as const,
  detail: (id: string, eventId?: string) =>
    [...bataillonBoardKeys.all, 'detail', id, eventId ?? 'all'] as const,
  candidates: (id: string, q: object) =>
    [...bataillonBoardKeys.all, 'candidates', id, q] as const,
  command: (roles: string) => [...bataillonBoardKeys.all, 'command', roles] as const,
};

/* ───────────── Leitura ───────────── */

export const useBataillonOverview = (eventId?: string) =>
  useQuery({
    queryKey: bataillonBoardKeys.overview(eventId),
    queryFn: async () =>
      unwrap(
        (await api.overview.get({ query: { eventId } })).data,
      ) as BataillonOverview[],
  });

export const useBataillonDetail = (id?: string, eventId?: string) =>
  useQuery({
    enabled: !!id,
    queryKey: bataillonBoardKeys.detail(id ?? '', eventId),
    queryFn: async () =>
      unwrap(
        (await api({ id }).detail.get({ query: { eventId } })).data,
      ) as BataillonDetail,
  });

/** Usuários que podem ocupar um cargo do comando (`major` ou `capitao`). */
export const useCommandOptions = (role: UserRole) =>
  useQuery({
    queryKey: bataillonBoardKeys.command(role),
    queryFn: async () =>
      unwrap(
        (await api.options.command.get({ query: { roles: role } })).data,
      ) as CommandPerson[],
    staleTime: 5 * 60_000,
  });

export const useLinkCandidates = (
  id: string,
  q: { search?: string; scope: 'free' | 'all' },
  enabled: boolean,
) =>
  useQuery({
    enabled,
    queryKey: bataillonBoardKeys.candidates(id, q),
    queryFn: async () =>
      unwrap(
        (
          await api({ id }).candidates.get({
            query: { search: q.search || undefined, scope: q.scope },
          })
        ).data,
      ) as BataillonMember[],
  });

/** Dossiê: busca as fichas completas só quando o usuário pede para emitir. */
export const fetchDossier = async (ids: string[], eventId?: string) =>
  unwrap((await api.dossier.post({ ids, eventId })).data) as BataillonDetail[];

/* ───────────── Escrita ───────────── */

/** O client do Eden lança `{ status, value }`; a API devolve `{ message, errors? }` em `value`. */
const errorMessage = (e: any) => firstErrorMessage(e?.value ?? e).message;

function useBoardMutation<TVars>(
  fn: (v: TVars) => Promise<unknown>,
  success?: (v: TVars, data: any) => string,
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data, v) => {
      if (success) toast.success(success(v, data));
    },
    onError: (e: any) => toast.error(errorMessage(e)),
    onSettled: () => qc.invalidateQueries({ queryKey: ['bataillons'] }),
  });
}

export type BataillonForm = {
  name: string;
  tag: string | null;
  isActive: boolean;
  majorId: string | null;
  kidsMajorId: string | null;
  kidsCaptainId: string | null;
  youthCaptainId: string | null;
  description: string | null;
};

export const useSaveBataillon = () =>
  useBoardMutation(
    async ({ id, data }: { id?: string; data: BataillonForm }) =>
      unwrap((id ? await api({ id }).patch(data) : await api.post(data)).data),
    () => 'Batalhão salvo',
  );

/** Missões: otimista na ficha (toggle/remover respondem na hora). */
export function useTaskMutations(id: string, eventId?: string) {
  const qc = useQueryClient();
  const key = bataillonBoardKeys.detail(id, eventId);
  const patchTasks = (fn: (t: BataillonTask[]) => BataillonTask[]) => {
    const prev = qc.getQueryData<BataillonDetail>(key);
    if (prev) qc.setQueryData(key, { ...prev, tasks: fn(prev.tasks) });
    return () => prev && qc.setQueryData(key, prev);
  };
  const settle = () => qc.invalidateQueries({ queryKey: ['bataillons'] });
  const toast = useToast();
  const fail = (e: any) => toast.error(errorMessage(e));
  return {
    add: useMutation({
      mutationFn: (v: { title: string; phase: TaskPhase }) => api({ id }).tasks.post(v),
      onError: fail,
      onSettled: settle,
    }),
    /** Troca a fase da missão (antes → no retiro → pós-retiro). */
    setPhase: useMutation({
      mutationFn: (v: { task: BataillonTask; phase: TaskPhase }) =>
        api({ id }).tasks({ taskId: v.task.id }).patch({ phase: v.phase }),
      onMutate: (v) => ({
        undo: patchTasks((all) =>
          all.map((x) => (x.id === v.task.id ? { ...x, phase: v.phase } : x)),
        ),
      }),
      onError: (e, _v, ctx) => {
        ctx?.undo?.();
        fail(e);
      },
      onSettled: settle,
    }),
    toggle: useMutation({
      mutationFn: (t: BataillonTask) =>
        api({ id }).tasks({ taskId: t.id }).patch({ done: !t.done }),
      onMutate: (t) => ({
        undo: patchTasks((all) =>
          all.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)),
        ),
      }),
      onError: (e, _v, ctx) => {
        ctx?.undo?.();
        fail(e);
      },
      onSettled: settle,
    }),
    remove: useMutation({
      mutationFn: (t: BataillonTask) => api({ id }).tasks({ taskId: t.id }).delete(),
      onMutate: (t) => ({ undo: patchTasks((all) => all.filter((x) => x.id !== t.id)) }),
      onError: (e, _v, ctx) => {
        ctx?.undo?.();
        fail(e);
      },
      onSettled: settle,
    }),
    reorder: useMutation({
      mutationFn: (ids: string[]) => api({ id }).tasks.order.put({ ids }),
      onError: fail,
      onSettled: settle,
    }),
  };
}

export const useLinkSoldiers = (id: string) =>
  useBoardMutation(
    (soldierIds: string[]) => api({ id }).soldiers.post({ soldierIds }),
    (ids) =>
      `${ids.length} ${ids.length === 1 ? 'recruta vinculado' : 'recrutas vinculados'}`,
  );

export const useUnlinkSoldier = (id: string) =>
  useBoardMutation(
    (s: { id: string; name: string }) =>
      api({ id }).soldiers({ soldierId: s.id }).delete(),
    (s) => `${s.name} desvinculado(a)`,
  );
